import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CreditCard, Truck, ShieldCheck, Loader2, Package, Mail, User } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { formatPrice, displayColor } from "../utils/formatters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AU_STATES = [
  { code: "NSW", name: "New South Wales" },
  { code: "VIC", name: "Victoria" },
  { code: "QLD", name: "Queensland" },
  { code: "SA", name: "South Australia" },
  { code: "WA", name: "Western Australia" },
  { code: "TAS", name: "Tasmania" },
  { code: "NT", name: "Northern Territory" },
  { code: "ACT", name: "Australian Capital Territory" },
];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { isAuthenticated, token, loading: authLoading } = useAuth();
  const { cart, loading: cartLoading, getCartTotal, getCartCount } = useCart();
  const [processing, setProcessing] = useState(false);
  const [shippingCost, setShippingCost] = useState(null);
  const [freeShipping, setFreeShipping] = useState(false);
  const [calcLoading, setCalcLoading] = useState(false);

  const [guestInfo, setGuestInfo] = useState({ email: "", name: "" });
  const [shipping, setShipping] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "Australia",
  });

  useEffect(() => {
    if (cart.items?.length > 0 && window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        num_items: getCartCount(),
        value: getCartTotal(),
        currency: 'AUD',
      });
    }
  }, []);

  const calcShipping = useCallback(async (state) => {
    if (!state) return;
    const qty = getCartCount();
    setCalcLoading(true);
    try {
      const res = await axios.post(`${API}/shipping/calculate`, { state, quantity: qty });
      setShippingCost(res.data.shipping_cost);
      setFreeShipping(res.data.free_shipping);
    } catch {
      setShippingCost(null);
      setFreeShipping(false);
    } finally {
      setCalcLoading(false);
    }
  }, [getCartCount]);

  useEffect(() => {
    if (shipping.state) {
      calcShipping(shipping.state);
    }
  }, [shipping.state, calcShipping]);

  if (authLoading || cartLoading) {
    return (
      <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#FFD700] animate-spin" />
      </div>
    );
  }

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-3xl mb-4">CART IS EMPTY</h2>
          <p className="text-[#A3A3A3] mb-8">Add some items before checkout</p>
          <Link to="/shop" className="btn-primary inline-block" data-testid="go-shop-btn">
            Go to Shop
          </Link>
        </div>
      </div>
    );
  }

  const subtotal = getCartTotal();
  const totalQuantity = getCartCount();
  const grandTotal = subtotal + (shippingCost || 0);

  const handleChange = (e) => {
    setShipping({ ...shipping, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!shipping.name || !shipping.address || !shipping.city || !shipping.state || !shipping.zip) {
      toast.error("Please fill in all shipping fields");
      return;
    }

    if (shippingCost === null) {
      toast.error("Please select a state to calculate shipping");
      return;
    }

    if (!isAuthenticated && (!guestInfo.email || !guestInfo.name)) {
      toast.error("Please enter your name and email");
      return;
    }

    setProcessing(true);
    try {
      let response;
      if (isAuthenticated) {
        response = await axios.post(
          `${API}/checkout/session`,
          { origin_url: window.location.origin, shipping_address: shipping, shipping_state: shipping.state },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.post(`${API}/checkout/guest-session`, {
          origin_url: window.location.origin,
          email: guestInfo.email,
          name: guestInfo.name,
          shipping_address: shipping,
          shipping_state: shipping.state,
          items: cart.items.map((i) => ({
            product_id: i.product_id,
            quantity: i.quantity,
            size: i.size,
            color: i.color,
          })),
        });
      }

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate checkout");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12" data-testid="checkout-page">
      <div className="max-w-[1200px] mx-auto">
        <Link
          to="/cart"
          className="inline-flex items-center gap-2 text-[#A3A3A3] hover:text-[#FFD700] transition-colors mb-8 font-mono text-sm tracking-wider"
          data-testid="back-to-cart-link"
        >
          <ArrowLeft className="w-4 h-4" />
          BACK TO CART
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-2">SECURE CHECKOUT</p>
          <h1 className="font-heading text-5xl md:text-7xl tracking-tight uppercase">PAYMENT</h1>
        </motion.div>

        {/* Free shipping banner */}
        {totalQuantity < 3 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-[#FFD700]/30 bg-[#FFD700]/5 px-6 py-3 mb-8 flex items-center gap-3"
          >
            <Package className="w-5 h-5 text-[#FFD700] flex-shrink-0" />
            <p className="font-mono text-sm text-[#FFD700]">
              ADD {3 - totalQuantity} MORE ITEM{3 - totalQuantity > 1 ? "S" : ""} FOR FREE SHIPPING!
            </p>
          </motion.div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left: Forms */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-7"
            >
              {/* Guest Info (only for non-logged-in users) */}
              {!isAuthenticated && (
                <div className="border border-white/10 p-8 mb-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Mail className="w-5 h-5 text-[#FFD700]" />
                    <h2 className="font-heading text-2xl">YOUR DETAILS</h2>
                  </div>
                  <div className="space-y-5">
                    <div>
                      <label className="block font-mono text-xs tracking-wider text-[#A3A3A3] mb-2">FULL NAME</label>
                      <div className="relative flex items-center">
                        <User className="absolute left-3 w-4 h-4 text-[#A3A3A3]" />
                        <input
                          type="text"
                          value={guestInfo.name}
                          onChange={(e) => setGuestInfo({ ...guestInfo, name: e.target.value })}
                          placeholder="John Doe"
                          className="w-full bg-transparent border border-white/20 px-4 py-3 pl-10 text-white placeholder-white/30 focus:border-[#FFD700] focus:outline-none transition-colors font-mono"
                          required
                          data-testid="guest-name"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block font-mono text-xs tracking-wider text-[#A3A3A3] mb-2">EMAIL</label>
                      <div className="relative flex items-center">
                        <Mail className="absolute left-3 w-4 h-4 text-[#A3A3A3]" />
                        <input
                          type="email"
                          value={guestInfo.email}
                          onChange={(e) => setGuestInfo({ ...guestInfo, email: e.target.value })}
                          placeholder="you@email.com"
                          className="w-full bg-transparent border border-white/20 px-4 py-3 pl-10 text-white placeholder-white/30 focus:border-[#FFD700] focus:outline-none transition-colors font-mono"
                          required
                          data-testid="guest-email"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Shipping Address */}
              <div className="border border-white/10 p-8 mb-8">
                <div className="flex items-center gap-3 mb-6">
                  <Truck className="w-5 h-5 text-[#FFD700]" />
                  <h2 className="font-heading text-2xl">SHIPPING ADDRESS</h2>
                </div>
                <div className="space-y-5">
                  <div>
                    <label className="block font-mono text-xs tracking-wider text-[#A3A3A3] mb-2">FULL NAME</label>
                    <input type="text" name="name" value={shipping.name} onChange={handleChange} placeholder="John Doe" className="w-full bg-transparent border border-white/20 px-4 py-3 text-white placeholder-white/30 focus:border-[#FFD700] focus:outline-none transition-colors font-mono" data-testid="shipping-name" />
                  </div>
                  <div>
                    <label className="block font-mono text-xs tracking-wider text-[#A3A3A3] mb-2">ADDRESS</label>
                    <input type="text" name="address" value={shipping.address} onChange={handleChange} placeholder="123 Main Street" className="w-full bg-transparent border border-white/20 px-4 py-3 text-white placeholder-white/30 focus:border-[#FFD700] focus:outline-none transition-colors font-mono" data-testid="shipping-address" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block font-mono text-xs tracking-wider text-[#A3A3A3] mb-2">CITY</label>
                      <input type="text" name="city" value={shipping.city} onChange={handleChange} placeholder="Sydney" className="w-full bg-transparent border border-white/20 px-4 py-3 text-white placeholder-white/30 focus:border-[#FFD700] focus:outline-none transition-colors font-mono" data-testid="shipping-city" />
                    </div>
                    <div>
                      <label className="block font-mono text-xs tracking-wider text-[#A3A3A3] mb-2">STATE / TERRITORY</label>
                      <select name="state" value={shipping.state} onChange={handleChange} className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white focus:border-[#FFD700] focus:outline-none transition-colors font-mono appearance-none cursor-pointer" data-testid="shipping-state">
                        <option value="">Select state...</option>
                        {AU_STATES.map((s) => (
                          <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block font-mono text-xs tracking-wider text-[#A3A3A3] mb-2">POSTCODE</label>
                      <input type="text" name="zip" value={shipping.zip} onChange={handleChange} placeholder="2000" className="w-full bg-transparent border border-white/20 px-4 py-3 text-white placeholder-white/30 focus:border-[#FFD700] focus:outline-none transition-colors font-mono" data-testid="shipping-zip" />
                    </div>
                    <div>
                      <label className="block font-mono text-xs tracking-wider text-[#A3A3A3] mb-2">COUNTRY</label>
                      <input type="text" name="country" value={shipping.country} disabled className="w-full bg-transparent border border-white/20 px-4 py-3 text-white/50 font-mono cursor-not-allowed" data-testid="shipping-country" />
                      <p className="font-mono text-xs text-[#A3A3A3] mt-1">Australia only</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-6 text-[#A3A3A3]">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#FFD700]" />
                  <span className="font-mono text-xs tracking-wider">SSL ENCRYPTED</span>
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-[#FFD700]" />
                  <span className="font-mono text-xs tracking-wider">SECURE PAYMENT</span>
                </div>
              </div>
            </motion.div>

            {/* Right: Order Summary */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="lg:col-span-5"
            >
              <div className="border border-[#FFD700]/30 p-8 sticky top-32">
                <h2 className="font-heading text-2xl mb-6 flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-[#FFD700]" />
                  ORDER SUMMARY
                </h2>

                <div className="space-y-4 border-b border-white/10 pb-6 mb-6 max-h-[300px] overflow-y-auto pr-2">
                  {cart.items.map((item) => (
                    <div key={`${item.product_id}-${item.size}-${item.color}`} className="flex gap-4" data-testid={`summary-item-${item.product_id}`}>
                      <div className="w-16 h-20 flex-shrink-0 border border-white/10">
                        <img src={item.product?.images?.[0]} alt={item.product?.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-heading text-sm truncate">{item.product?.name}</p>
                        <p className="text-[#A3A3A3] text-xs mt-1">{item.size} / {displayColor(item.color)} x{item.quantity}</p>
                      </div>
                      <p className="font-mono text-sm text-[#FFD700] flex-shrink-0">
                        {formatPrice(item.product?.price * item.quantity)}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="space-y-3 border-b border-white/10 pb-6 mb-6">
                  <div className="flex justify-between text-[#A3A3A3] text-sm">
                    <span className="font-mono">SUBTOTAL</span>
                    <span className="font-mono">{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-mono text-[#A3A3A3] flex items-center gap-2">
                      <Truck className="w-3.5 h-3.5" />SHIPPING
                    </span>
                    {calcLoading ? (
                      <Loader2 className="w-4 h-4 text-[#FFD700] animate-spin" />
                    ) : shippingCost === null ? (
                      <span className="font-mono text-[#A3A3A3] text-xs">SELECT STATE</span>
                    ) : freeShipping ? (
                      <span className="font-mono text-[#FFD700]">FREE</span>
                    ) : (
                      <span className="font-mono text-white">{formatPrice(shippingCost)}</span>
                    )}
                  </div>
                  {freeShipping && <p className="font-mono text-xs text-[#FFD700]">3+ items = free shipping!</p>}
                </div>

                <div className="flex justify-between text-xl mb-8">
                  <span className="font-heading">TOTAL</span>
                  <span className="font-mono text-[#FFD700]" data-testid="checkout-total">{formatPrice(grandTotal)}</span>
                </div>

                <button
                  type="submit"
                  disabled={processing || shippingCost === null}
                  className="w-full bg-[#FFD700] text-black font-heading text-lg py-4 tracking-wider hover:bg-[#FFC000] transition-colors disabled:opacity-50 flex items-center justify-center gap-3"
                  data-testid="pay-now-btn"
                >
                  {processing ? (
                    <><Loader2 className="w-5 h-5 animate-spin" />PROCESSING...</>
                  ) : (
                    <><ShieldCheck className="w-5 h-5" />PAY {formatPrice(grandTotal)}</>
                  )}
                </button>

                <p className="text-center text-[#A3A3A3] text-xs font-mono mt-4 tracking-wider">
                  Secure checkout powered by Stripe.
                </p>
              </div>
            </motion.div>
          </div>
        </form>
      </div>
    </div>
  );
}
